import { SECOND_MS } from '@calm/web-common/dist/time';
import { routes } from '@rhg/utils/routes';
import { jwtDecode } from 'jwt-decode';

export const PARTNER_ACTIVATION_STORAGE_KEY = 'PARTNER_MEMBER_ACTIVATION';
export const PARTNER_MEMBER_ACTIVATION_TOKEN_HEADER =
    'X-Partner-Member-Activation-Token';

export const PARTNER_ACTIVATION_COOKIE = 'partner_member_activation';
export const PARTNER_ACTIVATION_COOKIE_MAX_AGE_SECONDS = 300;
export const CACHE_CONTROL_NO_STORE = 'no-store';
export const REFERRER_POLICY_NO_REFERRER = 'no-referrer';

interface PartnerMemberActivationTokenHeader {
    partner_id?: unknown;
    exp?: unknown;
}

export interface ActivationTokenHeader {
    partnerId: string;
    expiresAt: number;
}

export interface StoredPartnerActivation {
    token: string;
    partnerId: string;
}

/**
 * The token-free activation route as served for `locale`. Sub-path routing
 * leaves the default locale unprefixed, so `/es/partner-member-activation` but
 * `/partner-member-activation` for English.
 */
export function getLocalizedActivationPath(
    locale?: string,
    defaultLocale?: string
): string {
    // Read per call so that importing this module never reads routes.
    const path = routes.partnerMemberActivation;
    return !locale || locale === defaultLocale ? path : `/${locale}${path}`;
}

function buildActivationCookie(
    value: string,
    maxAgeSeconds: number,
    path: string
): string {
    return [
        `${PARTNER_ACTIVATION_COOKIE}=${value}`,
        `Path=${path}`,
        'HttpOnly',
        'Secure',
        'SameSite=Lax',
        `Max-Age=${maxAgeSeconds}`,
    ].join('; ');
}

/** Set-Cookie value that carries `token` to the token-free activation route at `path`. */
export function buildActivationCookieHeader(
    token: string,
    path: string
): string {
    return buildActivationCookie(
        encodeURIComponent(token),
        PARTNER_ACTIVATION_COOKIE_MAX_AGE_SECONDS,
        path
    );
}

/** Set-Cookie value that expires the activation cookie set for `path`. */
export function buildActivationCookieExpiryHeader(path: string): string {
    return buildActivationCookie('', 0, path);
}

/**
 * Reads the unverified `partner_id` and `exp` header claims from an
 * activation token's JWE protected header. Returns `undefined` when either
 * is missing or the token is not decodable.
 */
export function getActivationTokenHeader(
    token: string
): ActivationTokenHeader | undefined {
    try {
        const header = jwtDecode<PartnerMemberActivationTokenHeader>(token, {
            header: true,
        });
        if (typeof header.partner_id !== 'string' || !header.partner_id) {
            return undefined;
        }
        if (typeof header.exp !== 'number') {
            return undefined;
        }
        return { partnerId: header.partner_id, expiresAt: header.exp };
    } catch {
        return undefined;
    }
}

/** `expiresAt` is a NumericDate (seconds); `now` is milliseconds. */
export function isActivationTokenExpired(
    expiresAt: number,
    now: number = Date.now()
): boolean {
    return expiresAt * SECOND_MS <= now;
}

export function getStoredPartnerActivation():
    | StoredPartnerActivation
    | undefined {
    try {
        const stored = sessionStorage.getItem(PARTNER_ACTIVATION_STORAGE_KEY);
        if (!stored) {
            return undefined;
        }
        const parsed = JSON.parse(stored) as StoredPartnerActivation;
        if (!parsed?.token || !parsed?.partnerId) {
            return undefined;
        }
        return parsed;
    } catch {
        return undefined;
    }
}

/** Returns false when sessionStorage throws (disabled or full), true otherwise. */
export function setStoredPartnerActivation(
    activation: StoredPartnerActivation
): boolean {
    try {
        sessionStorage.setItem(
            PARTNER_ACTIVATION_STORAGE_KEY,
            JSON.stringify(activation)
        );
        return true;
    } catch {
        return false;
    }
}

export function clearStoredPartnerActivation(): void {
    try {
        sessionStorage.removeItem(PARTNER_ACTIVATION_STORAGE_KEY);
    } catch {
        // sessionStorage throws when storage is disabled.
    }
}

/**
 * Backslash-escapes regex metacharacters so `value` matches literally inside
 * a `RegExp`.
 */
function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Redacts the token segment out of any `/partner-member-activation/<token>`
 * path, including its URL-encoded form (e.g. inside a `redirect` query
 * value), so the bearer token never reaches logs or analytics.
 */
export function redactActivationTokens(value: string): string {
    // Built per call so that importing this module never reads routes.
    const activationPath = routes.partnerMemberActivation;
    const activationPathSegment = activationPath.slice(1);
    return value
        .replace(
            new RegExp(`${escapeRegExp(activationPath)}/[^/?#]+`, 'g'),
            `${activationPath}/[redacted]`
        )
        .replace(
            new RegExp(
                `%2[fF]${escapeRegExp(
                    activationPathSegment
                )}%2[fF](?:(?!%2[fF]|[&#]).)+`,
                'g'
            ),
            `%2F${activationPathSegment}%2F[redacted]`
        );
}
